import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { DEFAULT_APPEARANCE, MOCK_CATALOG } from '../api/catalogData';
import type {
  Appearance, CharacterDraft, CharacterStats, CreationCatalog, ProfessionId
} from '../api/types';
import { AppearanceEditor } from '../components/AppearanceEditor';
import { AparenciaCriacao } from '../components/criacao/AparenciaCriacao';
import { Gump } from '../components/Gump';
import { numero, t } from '../i18n/idioma';
import { nomeDaProfissao, nomeDaSkill } from '../i18n/nomes';
import { carregarPersonalizacao } from '../look/personalizacao';
import { useSession } from '../state/SessionContext';

type Step = 'profession' | 'stats' | 'look';

const APARENCIA_INICIAL: Appearance = {
  ...DEFAULT_APPEARANCE,
  skinHue: 1010, hairStyle: 'ponytail', hairHue: 1107, beardStyle: 'short', beardHue: 1107
};

const emptyAdvancedSkills = [
  { id: 'tactics', value: 50 },
  { id: 'anatomy', value: 30 },
  { id: 'healing', value: 20 }
];

function applyProfession(draft: CharacterDraft, catalog: CreationCatalog, id: ProfessionId): CharacterDraft {
  const p = catalog.professions.find(x => x.id === id);
  return {
    ...draft,
    profession: id,
    stats: p?.stats ?? { str: 10, dex: 10, int: 10 },
    skills: p?.skills ? p.skills.map(s => ({ id: s.id, value: s.value })) : emptyAdvancedSkills
  };
}

export function CreateScreen() {
  const { shard, cancelCreate, enterWorld } = useSession();
  const [catalog, setCatalog] = useState<CreationCatalog | null>(null);
  const [step, setStep] = useState<Step>('profession');
  const [draft, setDraft] = useState<CharacterDraft>({
    name: '',
    sex: 'male',
    profession: 'assassin',
    stats: MOCK_CATALOG.professions[0].stats!,
    skills: MOCK_CATALOG.professions[0].skills!.map(s => ({ id: s.id, value: s.value })),
    appearance: { ...APARENCIA_INICIAL },
    homeCity: 'minoc'
  });
  const [personalizacaoFalhou, setPersonalizacaoFalhou] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keyRef = useRef<string | null>(null);

  useEffect(() => {
    let live = true;
    api.creationCatalog()
      .then(c => { if (live) setCatalog(c); })
      .catch(() => { if (live) setCatalog(MOCK_CATALOG); });
    carregarPersonalizacao().catch(err => {
      console.warn('[personalizacao]', err);
      if (live) setPersonalizacaoFalhou(true);
    });
    return () => { live = false; };
  }, []);

  const cat = catalog ?? MOCK_CATALOG;

  const goNext = () => {
    setError(null);
    setStep(s => {
      if (s === 'profession') return draft.profession === 'advanced' ? 'stats' : 'look';
      return 'look';
    });
  };

  /* Clicar na profissão já escolhe e segue: sem o "Continuar" (a avançada vai para atributos, as outras para a
     aparência). */
  const escolherProfissao = (id: ProfessionId) => {
    setError(null);
    setDraft(d => applyProfession(d, cat, id));
    setStep(id === 'advanced' ? 'stats' : 'look');
  };

  const goBack = () => {
    setError(null);
    if (step === 'profession') {
      cancelCreate();
      return;
    }
    setStep(s => s === 'look' && draft.profession === 'advanced' ? 'stats' : 'profession');
  };

  const confirm = async () => {
    if (!shard || busy || draft.name.trim().length < 2) return;
    setBusy(true);
    setError(null);
    keyRef.current ??= crypto.randomUUID();
    try {
      const criado = await api.createCharacter(shard.id, { ...draft, homeCity: 'minoc' }, keyRef.current);
      enterWorld(criado);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('personagem.naoCriou'));
      setBusy(false);
    }
  };

  const titles: Record<Step, string> = {
    profession: t('personagem.profissao'),
    stats: t('personagem.atributosESkills'),
    look: t('personagem.aparencia')
  };

  return (
    <main className="screen">
      <div className={`screen__inner ${step === 'look' ? 'screen__inner--criar' : 'screen__inner--wide'}`}>
        <div className="brand">
          <h1>{t('personagem.nascerEm', { lugar: shard?.name ?? 'Britannia' })}</h1>
          <p>{t('personagem.oReinoAceita')}</p>
        </div>

        <Gump title={titles[step]}>
          {step === 'profession' && (
            <div className="picklist">
              {cat.professions.map(p => (
                <button
                  key={p.id}
                  type="button"
                  className="pick"
                  aria-pressed={draft.profession === p.id}
                  onClick={() => escolherProfissao(p.id as ProfessionId)}
                >
                  <span className="pick__name">{nomeDaProfissao(p.id, p.label)}</span>
                  <span className="pick__meta">
                    {p.skills
                      ? p.skills.map(s => `${nomeDaSkill(s.id, s.label)} ${numero(s.value)}`).join(' · ')
                      : t('personagem.distribui')}
                  </span>
                </button>
              ))}
            </div>
          )}

          {step === 'stats' && (
            <StatsStep catalog={cat} stats={draft.stats} skills={draft.skills}
              onStats={stats => setDraft(d => ({ ...d, stats }))}
              onSkills={skills => setDraft(d => ({ ...d, skills }))} />
          )}

          {step === 'look' && (personalizacaoFalhou ? (
            <AppearanceEditor
              catalog={cat}
              sex={draft.sex}
              value={draft.appearance}
              roupa={false}
              nome={draft.name}
              onNome={name => setDraft(d => ({ ...d, name }))}
              onSex={sex => setDraft(d => ({
                ...d,
                sex,
                appearance: {
                  ...d.appearance,
                  hairStyle: sex === 'female' && d.appearance.hairStyle === 'none' ? 'short' : d.appearance.hairStyle,
                  beardStyle: sex === 'female' ? null : (d.appearance.beardStyle ?? 'none'),
                  beardHue: sex === 'female' ? null : d.appearance.beardHue
                }
              }))}
              onChange={appearance => setDraft(d => ({ ...d, appearance }))}
            />
          ) : (
            <AparenciaCriacao catalog={cat} shardNome={shard?.name ?? 'Britannia'}
              nome={draft.name} sex={draft.sex} value={draft.appearance}
              onNome={name => setDraft(d => ({ ...d, name }))}
              onSex={sex => setDraft(d => ({ ...d, sex }))}
              onChange={appearance => setDraft(d => ({ ...d, appearance }))} />
          ))}

          {error && <p className="notice" role="alert">{error}</p>}
        </Gump>

        <div className="row row--split">
          <button type="button" className="btn btn--ghost" onClick={goBack} disabled={busy}>
            {step === 'profession' ? t('personagem.voltarALista') : t('comum.voltar')}
          </button>
          {step === 'profession' ? null : step === 'look' ? (
            <button type="button" className="btn btn--primary" onClick={() => void confirm()}
              disabled={busy || draft.name.trim().length < 2}>
              {busy ? t('comum.criando') : t('personagem.criar')}
            </button>
          ) : (
            <button type="button" className="btn btn--primary" onClick={goNext}
              disabled={step === 'stats' && !statsOk(draft.stats, draft.skills)}>
              {t('comum.continuar')}
            </button>
          )}
        </div>
      </div>
    </main>
  );
}

function statsOk(stats: CharacterStats, skills: CharacterDraft['skills']): boolean {
  const sum = stats.str + stats.dex + stats.int;
  const skillSum = skills.reduce((s, x) => s + x.value, 0);
  const ids = new Set(skills.map(s => s.id));
  return sum === 80 && skillSum === 100 && ids.size === 3
    && [stats.str, stats.dex, stats.int].every(n => n >= 10 && n <= 60)
    && skills.every(s => s.value >= 1 && s.value <= 50);
}

function StatsStep({ catalog, stats, skills, onStats, onSkills }: {
  catalog: CreationCatalog;
  stats: CharacterStats;
  skills: CharacterDraft['skills'];
  onStats: (s: CharacterStats) => void;
  onSkills: (s: CharacterDraft['skills']) => void;
}) {
  const remain = 80 - stats.str - stats.dex - stats.int;
  const bump = (key: keyof CharacterStats, delta: number) => {
    const next = stats[key] + delta;
    if (next < 10 || next > 60) return;
    if (delta > 0 && remain <= 0) return;
    onStats({ ...stats, [key]: next });
  };

  return (
    <div className="stack">
      <p className="pick__meta">{t('personagem.restamPontos', { n: remain })}</p>
      {(['str', 'dex', 'int'] as const).map(key => (
        <div key={key} className="row row--split">
          <span className="field__label">
            {key === 'str' ? t('mundo.forca') : key === 'dex' ? t('mundo.destreza') : t('mundo.inteligencia')}
          </span>
          <div className="row">
            <button type="button" className="btn btn--tiny" onClick={() => bump(key, -1)}>-</button>
            <strong>{numero(stats[key])}</strong>
            <button type="button" className="btn btn--tiny" onClick={() => bump(key, 1)}>+</button>
          </div>
        </div>
      ))}
      <p className="pick__meta">{t('personagem.tresSkills')}</p>
      {skills.map((row, i) => (
        <div key={i} className="row">
          <select
            value={row.id}
            onChange={e => {
              const next = skills.map((s, j) => j === i ? { ...s, id: e.target.value } : s);
              onSkills(next);
            }}
          >
            {catalog.skills.map(s => <option key={s.id} value={s.id}>{nomeDaSkill(s.id, s.label)}</option>)}
          </select>
          <input
            type="number"
            min={1}
            max={50}
            value={row.value}
            onChange={e => {
              const value = Number(e.target.value);
              onSkills(skills.map((s, j) => j === i ? { ...s, value } : s));
            }}
          />
        </div>
      ))}
    </div>
  );
}
